import clsx from "clsx";
import React, { PropsWithChildren, useRef } from "react";

import { useScrollContainer } from "../../hooks/use-scroll-container";
import { CommonColors } from "../../types/colors";
import { Icon } from "../icon";

export interface ScrollableContainerProps {
  backgroundColor?: CommonColors;
  className?: string;
}

const iconContainerStyles: Record<CommonColors, string> = {
  [CommonColors.Primary]: "from-primary via-primary",
  [CommonColors.Light]: "from-light via-light",
  [CommonColors.Neutral]: "from-neutral via-neutral",
  [CommonColors.NeutralLight]: "from-neutral-light via-neutral-light",
  [CommonColors.Dark]: "from-dark via-dark",
  [CommonColors.Glass]: "from-glass via-glass",
  [CommonColors.Wood]: "from-wood via-wood",
  [CommonColors.Stone]: "from-stone via-stone",
  [CommonColors.Materia]: "from-materia via-materia",
  [CommonColors.Metal]: "from-metal via-metal",
};

export function ScrollableContainer({
  children,
  backgroundColor,
  className,
}: PropsWithChildren<ScrollableContainerProps>) {
  const iconContainerClasses = [
    "absolute top-0 flex h-full items-center w-10 z-10 to-transparent",
    backgroundColor ? iconContainerStyles[backgroundColor] : "from-transparent via-transparent",
  ];

  const tabsContainer = useRef<HTMLDivElement>(null);
  const {
    canScroll: shouldShowArrows,
    canScrollBackward: canScrollLeft,
    canScrollForward: canScrollRight,
    startScrolling,
    stopScrolling,
  } = useScrollContainer(tabsContainer, "horizontal");

  return (
    <div className="relative w-full overflow-hidden">
      {shouldShowArrows && (
        <div className={clsx(iconContainerClasses, "bg-gradient-to-r left-1")}>
          <Icon
            onMouseDown={() => startScrolling(-10)}
            onMouseUp={stopScrolling}
            icon="chevron-left"
            className={clsx(
              canScrollLeft ? "cursor-pointer" : "opacity-20",
              "text-primary transition-all"
            )}
          />
        </div>
      )}
      <div className={clsx("overflow-x-auto no-scrollbar", className)} ref={tabsContainer}>
        <div className="flex gap-2 items-center lg:w-fit lg:mx-auto">{children}</div>
      </div>
      {shouldShowArrows && (
        <div className={clsx(iconContainerClasses, "bg-gradient-to-l right-1 justify-end")}>
          <Icon
            onMouseDown={() => startScrolling(10)}
            onMouseUp={stopScrolling}
            icon="chevron-left"
            className={clsx(
              canScrollRight ? "cursor-pointer" : "opacity-20",
              "text-primary transition-all -scale-x-100"
            )}
          />
        </div>
      )}
    </div>
  );
}
